import type { PropsWithChildren } from 'react';
import React from 'react';

import { Box, Heading, Page } from '@zillow/constellation-10';

interface ContentContainerProps {
  configContent: PageWrapperConfigContent;
  children: React.ReactNode;
  className?: string;
}

const ContentContainer = ({ configContent, children, className }: ContentContainerProps) => (
  <Box
    className={className}
    css={{
      backgroundColor: configContent.backgroundColor,
      border: configContent.borderColor ? '1px solid' : undefined,
      borderColor: configContent.borderColor === 'none' ? 'transparent' : configContent.borderColor,
      borderRadius: configContent.borderRadius,
      marginBottom: 'md',
      padding: configContent.padding,
    }}
  >
    {children}
  </Box>
);

export interface PageWrapperConfig {
  backgroundColor: string;
  fluid: boolean;
}
/* colors must be constellation names */
const defaultConfig: PageWrapperConfig = {
  backgroundColor: 'backgroundLight',
  fluid: false,
};

export interface PageWrapperConfigContent {
  backgroundColor: string;
  borderRadius: string;
  borderColor: string;
  padding?: string;
}

/* colors must be constellation names */
const defaultConfigContent: PageWrapperConfigContent = {
  backgroundColor: 'white',
  borderRadius: '5px',
  borderColor: 'gray.300',
};
export interface PageWrapperProps {
  title: string;
  multipleBlocks?: boolean;
  alert?: JSX.Element;
  widget?: JSX.Element;
  backButton?: JSX.Element;
  configContent: Partial<PageWrapperConfigContent>;
  config: Partial<PageWrapperConfig>;
}

const PageWrapper = ({
  title,
  backButton,
  widget,
  config: pConfig,
  configContent: pContentConfig,
  children,
  multipleBlocks = false,
  alert,
}: PropsWithChildren<PageWrapperProps>) => {
  const config = {
    ...defaultConfig,
    ...pConfig,
  };

  const configContent = {
    ...defaultConfigContent,
    ...pContentConfig,
  };

  const { backgroundColor, fluid } = config;

  return (
    <Page.Root className="PageWrapper" css={{ backgroundColor }} fluid={fluid}>
      {backButton && <Page.Breadcrumb css={{ display: 'flex', alignItems: 'center' }}>{backButton}</Page.Breadcrumb>}
      <Page.Header css={{ fontSize: 'lg', marginBottom: '400' }}>
        <Heading level={5}>{title}</Heading>
        {widget}
      </Page.Header>
      {alert && (
        <Box css={{ paddingInline: 'md' }}>
          <Box>{alert}</Box>
        </Box>
      )}
      <Page.Content>
        {multipleBlocks ? (
          React.Children.toArray(children)
            .filter(Boolean)
            .map((child, i) => (
              <ContentContainer className="ContentContainer" configContent={configContent} key={i}>
                {child}
              </ContentContainer>
            ))
        ) : (
          <ContentContainer className="ContentContainer" configContent={configContent}>
            {children}
          </ContentContainer>
        )}
      </Page.Content>
    </Page.Root>
  );
};

export default PageWrapper;
